import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plane, Ship, Truck, ArrowRight, FileCheck, Warehouse,
  Check, X, Mail, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '@/components/ui/accordion';
import SEO from '@/components/SEO';
import VisitorSelector from '@/components/home/VisitorSelector';
import CoordinationFlow from '@/components/home/CoordinationFlow';
import TradeLaneMap from '@/components/home/TradeLaneMap';

const CORE_SERVICES = [
  { key: 'oceanFreight', icon: Ship, to: '/contact' },
  { key: 'airFreight', icon: Plane, to: '/contact' },
];

const SUPPORTING_SERVICES = [
  { key: 'roadTransport', icon: Truck, to: '/contact' },
  { key: 'customsCoordination', icon: FileCheck, to: '/contact' },
  { key: 'warehousingHandling', icon: Warehouse, to: '/contact' },
];

const TRUST_PILLAR_KEYS = ['experience', 'languages', 'singleContact', 'personal', 'transparency'];

const HomePage = () => {
  const { t } = useTranslation();

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Freight Forwarding",
    "provider": {
      "@type": "Organization",
      "name": "Elementary Cargo B.V."
    },
    "areaServed": [
      { "@type": "Place", "name": "Europe" },
      { "@type": "Place", "name": "South America" }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Freight Forwarding Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Ocean Freight" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Air Freight" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Road Transport" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Customs Coordination" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Warehousing and Handling" }}
      ]
    }
  };

  const renderServiceGroup = (group) => (
    <Accordion type="single" collapsible className="space-y-4">
      {group.map((service) => (
        <AccordionItem
          key={service.key}
          value={service.key}
          className="rounded-xl border border-platinum-light bg-white px-6"
        >
          <AccordionTrigger className="text-left">
            <span className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-lg bg-navy-900/5 flex items-center justify-center flex-shrink-0">
                <service.icon className="w-5 h-5 text-gold" aria-hidden="true" />
              </span>
              <span>
                <span className="block font-space text-lg font-bold text-navy-900">
                  {t(`homePage.services.${service.key}.title`)}
                </span>
                <span className="block text-sm text-slate-600 font-normal">
                  {t(`homePage.services.${service.key}.summary`)}
                </span>
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm font-semibold text-navy-900 mb-2">
              {t('homePage.services.detailLabel')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              {t(`homePage.services.${service.key}.detail`)}
            </p>
            <Link to={service.to}>
              <Button variant="outline" className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white px-6 py-3">
                {t(`homePage.services.${service.key}.cta`)}
                <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
              </Button>
            </Link>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  const whatWeDoItems = t('homePage.operatingModel.whatWeDo.items', { returnObjects: true }) || [];
  const whatWeDoNotItems = t('homePage.operatingModel.whatWeDoNot.items', { returnObjects: true }) || [];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="homepage">
      <SEO
        titleKey="seo.home.title"
        descriptionKey="seo.home.description"
        path="/"
        structuredData={serviceSchema}
      />

      {/* 1. Hero */}
      <section className="relative min-h-[90vh] flex items-center bg-navy-900 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0" aria-hidden="true">
          {/* Pure CSS background: radial depth + diagonal line pattern. No media assets. */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(135deg, #ffffff 0px, #ffffff 1px, transparent 1px, transparent 14px)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/80" />
        </div>

        <div className="container-custom relative z-10 py-20">
          <div className="max-w-4xl">
            <span className="inline-block text-gold font-space text-sm md:text-base tracking-widest uppercase pb-2 border-b-2 border-gold/60 mb-6">
              {t('homePage.hero.eyebrow')}
            </span>
            <h1 className="font-space text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.1] mb-6">
              {t('homePage.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-platinum-light leading-relaxed mb-4 max-w-2xl">
              {t('homePage.hero.description')}
            </p>
            <p className="text-platinum-light mb-10">{t('homePage.hero.languages')}</p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 px-8 py-6 text-lg font-semibold rounded-lg">
                  {t('homePage.hero.primaryCta')}
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-lg">
                  {t('homePage.hero.secondaryCta')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Visitor selector */}
      <VisitorSelector />

      {/* 3. Coordination flow */}
      <CoordinationFlow />

      {/* 4. Services connected around the shipment */}
      <section className="py-20 md:py-28 bg-offwhite" data-testid="services-section">
        <div className="container-custom">
          <div className="max-w-3xl mb-14">
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('homePage.services.eyebrow')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('homePage.services.title')}
            </h2>
            <p className="text-lg text-slate-600">{t('homePage.services.intro')}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h3 className="font-space text-sm font-semibold uppercase tracking-wider text-navy-900 mb-4">
                {t('homePage.services.coreLabel')}
              </h3>
              {renderServiceGroup(CORE_SERVICES)}
            </div>
            <div>
              <h3 className="font-space text-sm font-semibold uppercase tracking-wider text-navy-900 mb-4">
                {t('homePage.services.supportingLabel')}
              </h3>
              {renderServiceGroup(SUPPORTING_SERVICES)}
            </div>
          </div>

          <p className="mt-10 text-sm text-slate-600 max-w-3xl">
            {t('homePage.services.partnerDisclosure')}
          </p>

          <div className="mt-8">
            <Link to="/sectors">
              <Button variant="outline" className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white px-8 py-4">
                {t('homePage.services.sectorsCta')}
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. Europe and South America trade lane */}
      <TradeLaneMap />

      {/* 6. Trust pillars and operating clarity */}
      <section className="py-20 md:py-28 bg-white" data-testid="trust-section">
        <div className="container-custom">
          <div className="max-w-3xl mb-14">
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('homePage.trustPillars.title')}
            </h2>
            <p className="text-lg text-slate-600">{t('homePage.trustPillars.intro')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {TRUST_PILLAR_KEYS.map((key) => (
              <div key={key} className="rounded-xl border border-platinum-light bg-offwhite p-6 h-full">
                <h3 className="font-space text-lg font-bold text-navy-900 mb-2">
                  {t(`homePage.trustPillars.${key}.title`)}
                </h3>
                <p className="text-sm font-semibold text-gold-dark mb-3">
                  {t(`homePage.trustPillars.${key}.summary`)}
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t(`homePage.trustPillars.${key}.detail`)}
                </p>
              </div>
            ))}
          </div>

          <h3 className="font-space text-2xl font-bold text-navy-900 mb-8">
            {t('homePage.operatingModel.title')}
          </h3>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-xl border-2 border-platinum-light bg-offwhite p-8">
              <h4 className="font-space text-xl font-bold text-navy-900 mb-2">
                {t('homePage.operatingModel.whatWeDo.title')}
              </h4>
              <p className="text-sm text-slate-600 mb-6">
                {t('homePage.operatingModel.whatWeDo.mobileSummary')}
              </p>
              <ul className="space-y-4">
                {Array.isArray(whatWeDoItems) && whatWeDoItems.map((item, index) => (
                  <li key={`do-${index}`} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border-2 border-platinum-light bg-offwhite p-8">
              <h4 className="font-space text-xl font-bold text-navy-900 mb-2">
                {t('homePage.operatingModel.whatWeDoNot.title')}
              </h4>
              <p className="text-sm text-slate-600 mb-6">
                {t('homePage.operatingModel.whatWeDoNot.mobileSummary')}
              </p>
              <ul className="space-y-4">
                {Array.isArray(whatWeDoNotItems) && whatWeDoNotItems.map((item, index) => (
                  <li key={`dont-${index}`} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10">
            <Link to="/about">
              <Button variant="outline" className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white px-8 py-4">
                {t('homePage.operatingModel.cta')}
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. Final shipment CTA */}
      <section className="py-20 md:py-28 bg-navy-900" data-testid="final-cta">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-6">
              {t('homePage.finalCta.title')}
            </h2>
            <p className="text-xl text-platinum-light mb-4">{t('homePage.finalCta.intro')}</p>
            <p className="text-platinum-light mb-4">{t('homePage.finalCta.response')}</p>
            <p className="text-platinum-light mb-10">{t('homePage.finalCta.reassurance')}</p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 px-10 py-6 text-lg font-semibold rounded-lg">
                  {t('homePage.finalCta.primaryCta')}
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-10 py-6 text-lg font-semibold rounded-lg">
                  {t('homePage.finalCta.secondaryCta')}
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-platinum-light">
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold">
                <Phone className="w-5 h-5" aria-hidden="true" />
                <span>{t('homePage.finalCta.phone')}</span>
              </a>
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold">
                <Mail className="w-5 h-5" aria-hidden="true" />
                <span>{t('homePage.finalCta.email')}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
